import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Trash2, Loader2, AlertTriangle, Store } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/molecules/alert-dialog';
import { Button } from '@/components/atoms/button';
import { Label } from '@/components/atoms/label';
import { Textarea } from '@/components/atoms/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/atoms/select';
import { cn } from '@/lib/utils';
import { useShops } from '../hooks/use-shops';
import type { Shop } from '@/lib/types';

interface DeleteShopDialogProps {
  shop: Shop;
  onConfirm: (params: { reason?: string; targetShopRefId?: string }) => void;
  isLoading?: boolean;
  variant?: 'icon' | 'button';
  disabled?: boolean;
}

export function DeleteShopDialog({
  shop,
  onConfirm,
  isLoading = false,
  variant = 'icon',
  disabled = false,
}: DeleteShopDialogProps) {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const [reason, setReason] = React.useState('');
  const [targetShopRefId, setTargetShopRefId] = React.useState<string>('_none');

  const { data: shops = [] } = useShops();

  // Filter out the current shop from the list
  const otherShops = shops.filter(
    (s) => (s.refId || s.id) !== (shop.refId || shop.id)
  );

  const hasPendingOrders = (shop.orderCount || shop.totalOrders || 0) > 0;

  const handleConfirm = () => {
    onConfirm({
      reason: reason || undefined,
      targetShopRefId: targetShopRefId && targetShopRefId !== '_none' ? targetShopRefId : undefined,
    });
  };

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (!newOpen) {
      setReason('');
      setTargetShopRefId('_none');
    }
  };

  const trigger =
    variant === 'icon' ? (
      <Button
        variant="ghost"
        size="icon"
        className="h-8 w-8 text-muted-foreground hover:text-destructive"
        disabled={disabled}
      >
        <Trash2 className="h-4 w-4" />
      </Button>
    ) : (
      <Button variant="destructive" size="sm" disabled={disabled}>
        <Trash2 className="h-4 w-4 mr-2" />
        {t('common.delete')}
      </Button>
    );

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogTrigger asChild onClick={(e) => e.stopPropagation()}>
        {trigger}
      </AlertDialogTrigger>
      <AlertDialogContent onClick={(e) => e.stopPropagation()} className="max-w-md">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-destructive" />
            {t('shops.deleteShop')}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {t('shops.deleteShopDescription', {
              name: shop.name,
              defaultValue: `Êtes-vous sûr de vouloir supprimer "${shop.name}" ? Cette action est irréversible.`,
            })}
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-4 py-4">
          {/* Warning for pending orders */}
          {hasPendingOrders && (
            <div className="flex items-start gap-3 p-3 rounded-lg bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800">
              <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-500 shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-amber-800 dark:text-amber-200">
                  {t('shops.hasPendingOrders')}
                </p>
                <p className="text-amber-700 dark:text-amber-300 mt-1">
                  {t('shops.pendingOrdersWarning')}
                </p>
              </div>
            </div>
          )}

          {/* Target shop selector */}
          {otherShops.length > 0 && (
            <div className="space-y-2">
              <Label htmlFor="targetShop" className="flex items-center gap-2">
                <Store className="h-4 w-4" />
                {t('shops.transferOrdersTo')}
              </Label>
              <Select value={targetShopRefId} onValueChange={setTargetShopRefId}>
                <SelectTrigger id="targetShop">
                  <SelectValue placeholder={t('shops.selectTargetShop')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">
                    {t('shops.noTransfer')}
                  </SelectItem>
                  {otherShops.map((s) => (
                    <SelectItem key={s.refId || s.id} value={s.refId || s.id}>
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {t('shops.transferOrdersDescription')}
              </p>
            </div>
          )}

          {/* Reason textarea */}
          <div className="space-y-2">
            <Label htmlFor="reason">{t('shops.deleteReason')}</Label>
            <Textarea
              id="reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t('shops.deleteReasonPlaceholder')}
              rows={3}
              maxLength={300}
            />
            <p className="text-xs text-muted-foreground text-right">
              {reason.length}/300
            </p>
          </div>
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel disabled={isLoading}>
            {t('common.cancel')}
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={isLoading}
            className={cn('bg-destructive text-destructive-foreground hover:bg-destructive/90')}
          >
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                {t('common.deleting')}
              </>
            ) : (
              <>
                <Trash2 className="h-4 w-4 mr-2" />
                {t('common.delete')}
              </>
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
